import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function DashboardPreview() {
  return (
    <div className="bg-[#0b1230]/90 border border-blue-900/30 rounded-2xl p-5 shadow-2xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] text-slate-500 uppercase tracking-wider">Visão Geral</div>
          <div className="text-sm font-semibold text-slate-200 mt-0.5">Sua Empresa</div>
        </div>
        <span className="text-[10px] text-green-400 bg-green-900/30 px-2 py-1 rounded-full border border-green-800/40 flex items-center gap-1">
          <span className="w-1.5 h-1.5 bg-green-400 rounded-full inline-block" />
          Sistema ativo
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2.5 mb-4">
        {[
          { value: '142', label: 'Instrumentos', color: 'text-white' },
          { value: '89%',  label: 'Calibrados',   color: 'text-green-400' },
          { value: '8',    label: 'A vencer',      color: 'text-amber-400' },
        ].map(s => (
          <div key={s.label} className="bg-[#080d1a] rounded-xl p-3 border border-slate-800/50">
            <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
            <div className="text-[9px] text-slate-500 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="mb-4">
        <div className="flex justify-between text-[9px] text-slate-500 mb-1.5">
          <span>Status da frota</span><span>126 / 142</span>
        </div>
        <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden flex">
          <div className="h-full bg-green-500" style={{ width: '89%' }} />
          <div className="h-full bg-amber-500" style={{ width: '5%' }} />
          <div className="h-full bg-red-500"   style={{ width: '4%' }} />
        </div>
        <div className="flex gap-3 mt-1.5 text-[9px] text-slate-600">
          <span className="text-green-500">● Calibrados</span>
          <span className="text-amber-500">● A vencer</span>
          <span className="text-red-500">● Vencidos</span>
        </div>
      </div>

      <div>
        <div className="text-[9px] text-slate-500 uppercase tracking-wider mb-2">Próximos vencimentos</div>
        <div className="space-y-1">
          {[
            { tag: 'MAN-0012', desc: 'Manômetro PV-1',   days: 3,  cls: 'text-red-400'   },
            { tag: 'TMP-0089', desc: 'Termopar forno A', days: 12, cls: 'text-amber-400' },
            { tag: 'PRS-0034', desc: 'Transdutor P1',    days: 24, cls: 'text-yellow-400' },
          ].map(i => (
            <div key={i.tag} className="flex items-center gap-2 bg-[#080d1a] rounded-lg px-3 py-2">
              <span className="font-mono text-[10px] text-blue-300 w-16 flex-shrink-0">{i.tag}</span>
              <span className="text-[10px] text-slate-500 flex-1 truncate">{i.desc}</span>
              <span className={`text-[10px] font-bold flex-shrink-0 ${i.cls}`}>{i.days}d</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const FEATURES = [
  { icon: '🤖', label: 'Leitura de certificados com IA' },
  { icon: '📋', label: 'ISO 10012 compliant' },
  { icon: '🔔', label: 'Alertas automáticos de vencimento' },
  { icon: '📄', label: 'Laudos digitais com assinatura' },
];

export default function LoginPage() {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [sessionMsg, setSessionMsg] = useState('');
  const { login }    = useAuth();
  const navigate     = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('expired') === '1') {
      setSessionMsg('Sua sessão expirou. Faça login novamente.');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Credenciais inválidas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#080d1a]">

      {/* ── Painel esquerdo — marketing ────────────────────────────────────── */}
      <div className="hidden lg:flex w-[58%] flex-col justify-between p-14 relative overflow-hidden">

        {/* grade de pontos */}
        <div
          className="absolute inset-0 opacity-[0.035]"
          style={{
            backgroundImage: 'radial-gradient(#4f8ef7 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />
        {/* glow azul */}
        <div className="absolute top-1/4 left-1/3 w-[500px] h-[500px] bg-blue-700/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-72 h-72 bg-blue-900/10 rounded-full blur-2xl pointer-events-none" />

        {/* Logo */}
        <div className="relative z-10">
          <div className="text-2xl font-bold tracking-tight">
            <span className="text-blue-400">Calibra</span><span className="text-white">AI</span>
          </div>
          <div className="text-[11px] text-slate-500 mt-1 tracking-wide">Plataforma de Metrologia Industrial</div>
        </div>

        {/* Conteúdo central */}
        <div className="relative z-10 space-y-7">
          <div>
            <h2 className="text-[2rem] font-bold text-white leading-tight">
              Controle total sobre seus<br />
              <span className="text-blue-400">instrumentos de medição</span>
            </h2>
            <p className="text-slate-400 mt-3 text-sm leading-relaxed max-w-md">
              Gerencie calibrações, rastreabilidade e conformidade com inteligência
              artificial integrada — do certificado ao laudo em minutos.
            </p>
          </div>

          <DashboardPreview />

          <div className="flex flex-wrap gap-2">
            {FEATURES.map(f => (
              <span
                key={f.label}
                className="flex items-center gap-1.5 bg-blue-950/40 border border-blue-900/30 rounded-full px-3 py-1.5 text-[11px] text-slate-300"
              >
                {f.icon} {f.label}
              </span>
            ))}
          </div>
        </div>

        {/* Rodapé */}
        <div className="relative z-10 flex items-center gap-2">
          <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
          <p className="text-[11px] text-slate-600">
            Ainda não tem acesso?{' '}
            <a href="mailto:contato@calibraai.com.br" className="text-blue-400 hover:text-blue-300 transition-colors">
              Solicite uma demonstração →
            </a>
          </p>
        </div>
      </div>

      {/* ── Painel direito — formulário ─────────────────────────────────────── */}
      <div className="flex-1 flex flex-col justify-center px-8 md:px-16 bg-[#0c1121] border-l border-slate-800/40">

        {/* Logo mobile */}
        <div className="lg:hidden mb-10">
          <div className="text-xl font-bold">
            <span className="text-blue-400">Calibra</span><span className="text-white">AI</span>
          </div>
        </div>

        <div className="max-w-sm w-full mx-auto">
          <h1 className="text-2xl font-bold text-white mb-1">Bem-vindo de volta</h1>
          <p className="text-slate-500 text-sm mb-8">Acesse sua plataforma de metrologia</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[10px] font-semibold text-slate-400 mb-1.5 uppercase tracking-widest">
                E-mail
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="seu@email.com"
                className="w-full px-4 py-3 bg-[#080d1a] border border-slate-700/50 rounded-xl text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-colors text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-[10px] font-semibold text-slate-400 mb-1.5 uppercase tracking-widest">
                Senha
              </label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-4 py-3 bg-[#080d1a] border border-slate-700/50 rounded-xl text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-colors text-sm"
                required
              />
            </div>

            {sessionMsg && (
              <div className="flex items-center gap-2 text-amber-400 text-sm bg-amber-950/30 border border-amber-900/30 rounded-xl px-3 py-2.5">
                <span className="text-base">⏱</span>
                <span>{sessionMsg}</span>
              </div>
            )}

            {error && (
              <div className="flex items-center gap-2 text-red-400 text-sm bg-red-950/30 border border-red-900/30 rounded-xl px-3 py-2.5">
                <span className="text-base">⚠</span>
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 mt-2 bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white font-semibold rounded-xl transition-all disabled:opacity-50 shadow-lg shadow-blue-900/30 text-sm"
            >
              {loading ? 'Entrando...' : 'Acessar Plataforma →'}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-slate-800/60">
            <p className="text-[11px] text-slate-600 text-center">
              Precisa de acesso?{' '}
              <a href="mailto:contato@calibraai.com.br" className="text-blue-400 hover:text-blue-300 transition-colors">
                Fale com nossa equipe
              </a>
            </p>
          </div>

          {/* badges */}
          <div className="flex items-center justify-center gap-4 mt-6">
            {['ISO 10012', 'Multi-tenant', 'IA integrada'].map(b => (
              <span key={b} className="text-[9px] text-slate-600 border border-slate-800 rounded-full px-2 py-0.5">
                {b}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
